import { CertificateStatus } from '@/@types/generated'
import * as dateFns from 'date-fns'

interface RenewableCertificate {
  status?: CertificateStatus
  expirationDate?: string | Date | null
  effectiveDate?: string | Date | null
  interstateChangeDate?: string | Date | null
  isInterstate?: boolean
}

// The 180-day window to renew an expired certificate runs from its expiration date, or from
// the interstate-to-intrastate change date when the change expired this certificate, whatever
// the expiration date says (CRD-029). A certificate issued after the change was not expired by
// it. Mirrors the backend's Certificate.LateRenewalDeadline.
function lateRenewalWindowStart(
  certificate: RenewableCertificate
): string | Date | null | undefined {
  const { expirationDate, effectiveDate, interstateChangeDate } = certificate
  if (!interstateChangeDate) return expirationDate
  if (effectiveDate && dateFns.isBefore(interstateChangeDate, effectiveDate))
    return expirationDate
  return interstateChangeDate
}

export function isRenewable(certificate: RenewableCertificate): boolean {
  // Interstate carriers do not renew certificates
  if (certificate?.isInterstate) return false

  // Status based checks...
  switch (certificate.status) {
    // Active: can renew starting 60 days before expiration
    case CertificateStatus.ACTIVE:
      return (
        !!certificate.expirationDate &&
        dateFns.isAfter(
          new Date(),
          dateFns.sub(certificate.expirationDate, { days: 60 })
        )
      )
    // Expired: within the 180-day grace period after expiration, or after an
    // interstate-to-intrastate change whatever the expiration date says (CRD-029)
    case CertificateStatus.EXPIRED: {
      const windowStart = lateRenewalWindowStart(certificate)
      return (
        !!windowStart &&
        dateFns.isBefore(new Date(), dateFns.add(windowStart, { days: 180 }))
      )
    }
  }

  return false
}
